import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { imgUrl } from '../utils';
import api from '../api';
import './Basket.css';

export default function Basket() {
  const { items, updateQty, removeItem, clearCart, totalAmount, totalItems } = useCart();
  const { isBuyer } = useAuth();
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [orderPlaced, setOrderPlaced] = useState(null);
  const navigate = useNavigate();

  if (!isBuyer) return (
    <div className="basket-page">
      <div className="container basket-empty">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
        <h2>Buyer Account Required</h2>
        <p>You need an Authorised Buyer account to add items and place orders.</p>
        <Link to="/register" className="btn-terracotta">Register as Buyer</Link>
      </div>
    </div>
  );

  if (orderPlaced) return (
    <div className="basket-page">
      <div className="container basket-empty">
        <div className="order-success-icon">✓</div>
        <h2>Order Submitted</h2>
        <p>Your order #{orderPlaced} has been submitted. We'll review it and send you a proforma invoice by email. Please arrange bank transfer payment on receipt of the invoice.</p>
        <div style={{display:'flex',gap:'12px',marginTop:'24px'}}>
          <Link to="/account" className="btn-primary">View My Orders</Link>
          <Link to="/catalogue" className="btn-secondary">Continue Shopping</Link>
        </div>
      </div>
    </div>
  );

  if (items.length === 0) return (
    <div className="basket-page">
      <div className="container basket-empty">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
        <h2>Your basket is empty</h2>
        <p>Browse the catalogue to add products.</p>
        <Link to="/catalogue" className="btn-primary">Browse Catalogue</Link>
      </div>
    </div>
  );

  const submit = async () => {
    setError('');
    setSubmitting(true);
    try {
      const r = await api.post('/orders', {
        items: items.map(i => ({ product_id: i.product_id, quantity: i.quantity })),
        notes
      });
      clearCart();
      setOrderPlaced(r.data.order_id);
    } catch(err) {
      setError(err.response?.data?.error || 'Failed to place order. Please try again.');
    }
    setSubmitting(false);
  };

  return (
    <div className="basket-page">
      <div className="basket-header">
        <div className="container">
          <p className="tag">Your Selection</p>
          <h1 className="basket-title">Order Basket</h1>
          <p style={{fontSize:'13px',color:'var(--gray)',marginTop:'6px'}}>{totalItems} item{totalItems !== 1 ? 's' : ''}</p>
        </div>
      </div>

      <div className="container basket-body">
        <div className="basket-items">
          {items.map(item => (
            <div key={item.product_id} className="basket-item">
              <div className="basket-item-img">
                {item.image
                  ? <img src={imgUrl(item.image)} alt={item.name} />
                  : <div className="basket-img-placeholder" />
                }
              </div>
              <div className="basket-item-info">
                <h4 className="basket-item-name">{item.name}</h4>
                {item.colours && <p className="basket-item-sub">{item.colours}</p>}
                <p className="basket-item-price">£{Number(item.price).toFixed(2)} per unit</p>
                <p className="basket-item-price" style={{fontSize:'11px',color:'var(--terracotta)'}}>Min. order: {item.min_order_qty} units</p>
              </div>
              <div className="basket-item-qty">
                <button onClick={() => updateQty(item.product_id, item.quantity - item.min_order_qty, item.min_order_qty)}>−</button>
                <input
                  type="number"
                  value={item.quantity}
                  min={item.min_order_qty}
                  step={item.min_order_qty}
                  onChange={e => updateQty(item.product_id, parseInt(e.target.value) || item.min_order_qty, item.min_order_qty)}
                  style={{width:'60px',textAlign:'center',border:'1px solid var(--gray-light)',padding:'4px 8px',fontSize:'14px'}}
                />
                <button onClick={() => updateQty(item.product_id, item.quantity + item.min_order_qty, item.min_order_qty)}>+</button>
              </div>
              <div className="basket-item-total">£{(item.price * item.quantity).toFixed(2)}</div>
              <button className="basket-remove" onClick={() => removeItem(item.product_id)}>✕</button>
            </div>
          ))}
        </div>

        <div className="basket-summary">
          <h3 className="summary-title">Order Summary</h3>
          <div className="summary-rows">
            {items.map(i => (
              <div key={i.product_id} className="summary-row">
                <span>{i.name} × {i.quantity}</span>
                <span>£{(i.price * i.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="summary-total">
            <span>Total</span>
            <span>£{totalAmount.toFixed(2)}</span>
          </div>
          <div className="form-group" style={{marginTop:'20px'}}>
            <label>Order Notes (optional)</label>
            <textarea rows={3} value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Any special instructions..." />
          </div>
          <div className="alert alert-info" style={{fontSize:'12px'}}>
            Payment by bank transfer only. A proforma invoice will be emailed to you after order confirmation.
          </div>
          {error && <div className="alert alert-error">{error}</div>}
          <button className="btn-terracotta" style={{width:'100%',justifyContent:'center',padding:'16px'}} onClick={submit} disabled={submitting}>
            {submitting ? 'Placing Order...' : 'Place Order'}
          </button>
          <button className="basket-clear" onClick={clearCart}>Clear basket</button>
        </div>
      </div>
    </div>
  );
}
